import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Tabs,
  Select,
  Checkbox,
  Alert,
  DatePicker,
  TimePicker,
  Popover,
  ConfigProvider,
} from "antd";
import esES from "antd/locale/es_ES";
import {
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
  //UserAddOutlined,
} from "@ant-design/icons";
import "./aseos.css";
import "../../Css/aseos-movil.css";
import Layout from "../../Components/Layout";
import { formatPropertyName } from "../../utils/propertyDisplay";
import EstadiaCodeTag from "../../Components/EstadiaCodeTag";
import AseoCodeTag from "../../Components/AseoCodeTag";
import {
  formatEstadiaCode,
  parseEstadiaQuery,
} from "../../utils/estadiaCode";
import PageHeader from "../../Components/PageHeader";
import { useAseosLogic } from "./useAseosLogic";
import { CalendarOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { isAdminRole, normalizeRole } from "../../utils/roles.utils";
import dayjs from "dayjs";
import AseosCalendar from "./AseosCalendar";
import ImageCarouselModal, {
  type ImagenCarrusel,
} from "./components/ImageCarouselModal";
import { imagenesParaCarrusel } from "./imagenesCarrusel";
import NotasContenedor from "./components/NotasContenedor";
import AseoNotesSection, {
  type AseoNotesDraftHandle,
} from "./components/AseoNotesSection";
import TareasViewModal, {
  type TareasModalRecord,
} from "./components/TareasViewModal";
import { useSearchParams } from "react-router-dom";
import AseoFilterBar, {
  type AseoFilterValues,
  initialAseoFilterValues,
} from "./components/AseoFilterBar";
import { ANCHO_TABLA_ADMIN, buildAseosColumns } from "./components/aseosColumns";
import AseoTarjetasMovil from "./components/AseoTarjetasMovil";
import AvisoVentanaAbiertos from "./components/AvisoVentanaAbiertos";
import { useEsMovil, useEsTactil } from "./components/useMediaQuery";
import { pestanaInicialAseadora } from "./pestanaInicial";
import { useSanearFiltroSector } from "./useSanearFiltroSector";
import BulkActionsBar from "./components/BulkActionsBar";
import ActiveAseoFilters from "./components/ActiveAseoFilters";
import QuickAseoFilters from "./components/QuickAseoFilters";
import RepartoAseadorasField from "./components/RepartoAseadorasField";
import { aseosApi } from "../../services/aseos.api";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarRango,
  serializarRango,
} from "../../hooks/filtrosPersistidos";
const { TabPane } = Tabs;
const SUELDO_MIN = 0;
const SUELDO_MAX = 200_000;
const COSTO_MIN = 0;
const COSTO_MAX = 200_000;

const normalizeText = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFD") // separa letras de tildes
    .replace(/[\u0300-\u036f]/g, "") // elimina tildes
    .trim();

const textSimilar = (source: string, query: string) => {
  if (!query) return true;

  const src = normalizeText(source);
  const q = normalizeText(query);

  const tokens = q.split(/\s+/); // palabras buscadas

  // todas las palabras deben aparecer en algún lado
  return tokens.every((t) => src.includes(t));
};

const HIDDEN_COLUMN_KEYS = ["tareas", "imagenes"];

// ── Recordar los filtros (round 2026-09-24) ──────────────────────────────
// Los filtros de esta página se guardan por usuario admin en este navegador
// (ver hooks/useFiltroPersistido.ts). `fechaInicio` son Dayjs y no sobreviven
// a JSON: se guardan como "YYYY-MM-DD" y se reconstruyen al leer. El resto se
// rearma campo por campo, así un valor guardado por una versión anterior de la
// app no puede dejar la página en un estado imposible (un estado que ya no
// existe filtraría siempre a cero, sin nada en la UI que lo explique).
const URGENCIAS_VALIDAS = ["am", "pm", "critico 11:00", "11:00"];
const ESTADOS_VALIDOS = [
  "pendiente",
  "en_proceso",
  "completado",
  "pagado",
  "cancelado",
];
const ES_HORA = /^\d{2}:\d{2}$/;

const textosGuardados = (crudo: unknown, validos?: string[]): string[] =>
  Array.isArray(crudo)
    ? crudo.filter(
        (x): x is string =>
          typeof x === "string" && (!validos || validos.includes(x)),
      )
    : [];

const numerosGuardados = (crudo: unknown): [number | null, number | null] => {
  const uno = (x: unknown) =>
    typeof x === "number" && Number.isFinite(x) ? x : null;
  return Array.isArray(crudo) && crudo.length === 2
    ? [uno(crudo[0]), uno(crudo[1])]
    : [null, null];
};

const horasGuardadas = (crudo: unknown): [string | null, string | null] => {
  const una = (x: unknown) =>
    typeof x === "string" && ES_HORA.test(x) ? x : null;
  return Array.isArray(crudo) && crudo.length === 2
    ? [una(crudo[0]), una(crudo[1])]
    : [null, null];
};

const serializarFiltrosAseos = (f: AseoFilterValues): unknown => ({
  ...f,
  fechaInicio: serializarRango(f.fechaInicio),
});

const deserializarFiltrosAseos = (
  crudo: unknown,
): AseoFilterValues | undefined => {
  if (!crudo || typeof crudo !== "object") return undefined;
  const g = crudo as Record<string, unknown>;
  return {
    fechaInicio: (deserializarRango(g.fechaInicio) ?? [null, null]) as [
      any,
      any,
    ],
    aseadores: textosGuardados(g.aseadores),
    propiedad: textosGuardados(g.propiedad),
    sector: textosGuardados(g.sector),
    horario: horasGuardadas(g.horario),
    sueldoAseador: numerosGuardados(g.sueldoAseador),
    costoLimpieza: numerosGuardados(g.costoLimpieza),
    urgencia: textosGuardados(g.urgencia, URGENCIAS_VALIDAS),
    estado: textosGuardados(g.estado, ESTADOS_VALIDOS),
    observaciones: typeof g.observaciones === "string" ? g.observaciones : "",
  };
};

const AseosPage: React.FC = () => {
  const { user } = useAuth();
  // Ref al editor de las 3 notas en MODO BORRADOR (modal de creación de aseo).
  // El hook llama a `commit(nuevoId)` tras crear el aseo para persistir texto +
  // archivos staged. Ver AseoNotesSection (modo borrador) y useAseosLogic.
  const notesDraftRef = useRef<AseoNotesDraftHandle | null>(null);
  const {
    // States
    filteredAseadoras,
    //filteredAseos,
    loadingAseos,
    loadingProperties,
    aseoModalVisible,
    aseoModalNonce,
    submittingAseo,
    propertyModalVisible,
    editingAseo,
    sidebarOpen,
    aseoForm,
    aseosDisponibles, // para aseadores
    misAseos,
    aseosTerminados,
    selectedProperty,
    propertySearchText,
    aseoAssignmentMode,

    // Constants
    sectors,

    // Methods
    toggleSidebar,
    //toggleAdminSimulation,
    showAseoModal,
    handleAseoCancel,
    handleAseoSubmit,
    handleDeleteAseo,
    handleMarcarTodoPagado,
    setAseoAssignmentMode,
    openPropertySelector,
    handleSelectProperty,
    getFilteredProperties,
    setPropertySearchText,
    properties,
    tomarAseo,
    setPropertyModalVisible,
    fetchAseos,
    ventanaDias,
  } = useAseosLogic(user, notesDraftRef);

  // `user.rol` string escalar (ver CLAUDE.md §3.3 y WARNING en roles.utils.ts).
  const isAdmin = isAdminRole(user?.rol);
  const isAseador = normalizeRole(user?.rol) === "aseador";
  // setProcessingPago no se usa actualmente; preservamos solo el getter para el loading del botón.
  const [processingPago] = useState(false);
  const { Option } = Select;
  // Round 2026-04-30 octies — default 50 (antes 5). El admin típicamente
  // tiene decenas de aseos en curso y paginar de 5 en 5 es ruido. El tamaño
  // elegido se recuerda; el NÚMERO de página no (volver y aterrizar en la
  // página 7 con otro filtro muestra una tabla vacía).
  const [pageSizeAbiertos, setPageSizeAbiertos] = useFiltroPersistido(
    "aseos.filas",
    50,
    { validar: (v) => typeof v === "number" && v > 0 && v <= 200 },
  );

  const [searchParams, setSearchParams] = useSearchParams();
  // Un enlace directo manda sobre lo guardado. Se calcula UNA sola vez: el
  // efecto de más abajo borra estos params de la URL apenas monta y, si esto
  // se recalculara, el filtro guardado volvería a pisar el que traía el
  // enlace. Ver docs/filtros-persistentes.md.
  const [enlaceDirecto] = useState(() => ({
    filtros: Boolean(
      searchParams.get("sector") ||
        searchParams.get("aseador") ||
        (searchParams.get("desde") && searchParams.get("hasta")),
    ),
    pestana: Boolean(searchParams.get("aseador")) || searchParams.get("tab") === "abiertos",
  }));

  // Vista recordada: el admin que trabaja en el calendario vuelve al
  // calendario. Solo se aceptan los dos modos que existen hoy. Con enlace
  // directo arranca en "tabla": el filtro que trae la URL se aplica a
  // `filterValues`, pero la barra de filtros, los chips y las pestañas viven
  // DENTRO de la vista tabla — en calendario se filtraría sin que se monte
  // nada que lo muestre. Lo que el admin elija después se sigue guardando.
  // Ver docs/filtros-persistentes.md.
  const [aseosViewMode, setAseosViewMode] = useFiltroPersistido<
    "tabla" | "calendario"
  >("aseos.vista", "tabla", {
    validar: (v) => v === "tabla" || v === "calendario",
    restaurar: !enlaceDirecto.filtros,
  });

  // Round 2026-07-06 — la gestión de aseadores se movió a su propia página
  // (/aseadores, sección Gestión). /aseos quedó solo con la agenda de aseos;
  // se eliminó el Segmented de sub-tabs "Gestión de aseos / de aseadores".
  // Pestaña activa de la tabla de aseos (controlada). Antes era no-controlada
  // (defaultActiveKey="1"), pero al redirigir desde /aseadores con el
  // deep-link `?aseador=` necesitamos aterrizar en la pestaña que efectivamente
  // tiene los aseos del aseador (suelen estar en "Por hacer"/"Terminados", no
  // en "Abiertos").
  const [aseosTab, setAseosTab, setAseosTabSinRecordar] = useFiltroPersistido<
    "1" | "2" | "3"
  >(
    "aseos.pestana",
    "1",
    {
      validar: (v) => v === "1" || v === "2" || v === "3",
      restaurar: !enlaceDirecto.pestana,
    },
  );
  // Apertura del acordeón "Filtros avanzados" (controlada). Nada lo abre
  // solo: al llegar por enlace directo (`?sector=`, `?aseador=`,
  // `?desde=&hasta=`) el filtro ya se ve en la barra rápida y en los chips,
  // así que el panel no se toca. Si aparece abierto es porque este admin lo
  // dejó abierto: es el valor guardado en `aseos.panelAvanzado`.
  const [filterPanelOpen, setFilterPanelOpen] = useFiltroPersistido(
    "aseos.panelAvanzado",
    false,
    { validar: (v) => typeof v === "boolean" },
  );
  const [pageAbiertos, setPageAbiertos] = useState(1);
  const [pagePorHacer, setPagePorHacer] = useState(1);
  const [pageTerminados, setPageTerminados] = useState(1);
  // Round 2026-05-12 iter 39: selección bulk por tabla. Cada tab guarda
  // los ids seleccionados; el componente BulkActionsBar aparece encima
  // cuando hay selección y dispara las mutaciones via los handlers
  // `handleBulk*` definidos abajo.
  const [selectedAbiertos, setSelectedAbiertos] = useState<number[]>([]);
  const [selectedPorHacer, setSelectedPorHacer] = useState<number[]>([]);
  const [selectedTerminados, setSelectedTerminados] = useState<number[]>([]);
  // El getter no se usa: solo necesitamos el setter para sincronizar el slider con los filtros.
  const [, setUiCosto] = useState<[number, number]>([
    COSTO_MIN,
    COSTO_MAX,
  ]);
  const [, setUiSueldo] = useState<[number, number]>([
    SUELDO_MIN,
    SUELDO_MAX,
  ]);
  // Round 2026-05-12 — deeplink `/aseos?sector=<nombre>`: cuando el admin
  // navega desde la Card de sector en /finanzas-empresa, pre-aplicamos el
  // filtro de sector. El nombre viaja URL-encoded; lo decodificamos vía
  // URLSearchParams (que es transparente al respecto). El init usa una
  // función para correr una sola vez al montar (StrictMode safe).
  const [filterValues, setFilterValues, setFilterValuesSinRecordar] =
    useFiltroPersistido<AseoFilterValues>(
    "aseos.filtros",
    () => {
    // Deep-links soportados:
    //   /aseos?sector=<nombre>       → desde la Card de sector en /finanzas-empresa.
    //   /aseos?aseador=<nombre>      → desde /aseadores ("Ver aseos (N)") para
    //                                  reasignar/quitar los aseos de un aseador
    //                                  antes de poder eliminarlo (round 2026-07-06).
    //   /aseos?desde=&hasta=         → rango de fechas (YYYY-MM-DD) para el filtro
    //                                  `fechaInicio`. Lo usa /finanzas-empresa →
    //                                  Aseos para que "Ver detalle" muestre los
    //                                  aseos DEL MISMO período (round 2026-07-14).
    // Los nombres viajan URL-encoded; URLSearchParams los decodifica.
    let init = initialAseoFilterValues;
    const sectorParam = searchParams.get("sector");
    if (sectorParam && sectorParam.trim().length > 0) {
      init = { ...init, sector: [sectorParam.trim()] };
    }
    const aseadorParam = searchParams.get("aseador");
    if (aseadorParam && aseadorParam.trim().length > 0) {
      init = { ...init, aseadores: [aseadorParam.trim()] };
    }
    const desdeParam = searchParams.get("desde");
    const hastaParam = searchParams.get("hasta");
    if (desdeParam && hastaParam) {
      const d = dayjs(desdeParam);
      const h = dayjs(hastaParam);
      if (d.isValid() && h.isValid()) {
        init = { ...init, fechaInicio: [d.startOf("day"), h.endOf("day")] };
      }
    }
    return init;
    },
    {
      // Caduca junto con el rango de fechas que lleva adentro: restaurar el
      // mes pasado abre la agenda "vacía" sin nada que lo explique.
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: serializarFiltrosAseos,
      deserializar: deserializarFiltrosAseos,
      restaurar: !enlaceDirecto.filtros,
    },
  );

  // Nombres de sector que un ENLACE puso con la página ya abierta (tocar un aviso de la campana estando en
  // /aseos): se respetan aunque el sector ya no exista; ver useSanearFiltroSector.
  const sectoresDeEnlace = useRef<ReadonlySet<string>>(new Set());

  // Al llegar con `?aseador=`, aterrizamos en la pestaña indicada por `?tab=`
  // (la calcula /aseadores según dónde estén los aseos del aseador: "Por hacer"
  // o "Terminados"; un asignado nunca está en "Abiertos"). Default: "Por hacer".
  // El filtro de aseador ya es visible en la barra rápida (y como chip), así que
  // no hace falta abrir el panel avanzado.
  // La pestaña la impone el enlace, no la eligió el admin: va con el setter que
  // NO guarda, o el enlace le dejaría fijada esa pestaña como preferida.
  useEffect(() => {
    if (searchParams.get("aseador")) {
      const tab = searchParams.get("tab");
      setAseosTabSinRecordar(tab === "1" || tab === "3" ? tab : "2");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aseadora en celular: en vez de la tabla de ~1300 px, tarjetas (el botón
  // «Notas» y las acciones quedaban fuera de pantalla). El admin y el escritorio
  // conservan la tabla. Ver components/AseoTarjetasMovil.tsx.
  const esMovil = useEsMovil();
  const esTactil = useEsTactil();
  const vistaTarjetas = isAseador && esMovil;

  // Pestaña inicial de la aseadora en celular: la de SUS aseos («por hacer») si
  // los tiene; se decide una sola vez con la primera carga y solo si ella no
  // tocó ninguna pestaña antes. Escritorio y admin: sin cambios.
  const eligioPestana = useRef(false);
  const pestanaInicialResuelta = useRef(false);
  const cargaIniciadaVista = useRef(false);
  const cambiarPestana = (k: string) => {
    eligioPestana.current = true;
    setAseosTab(k as "1" | "2" | "3");
  };
  useEffect(() => {
    // La bandera se levanta aunque aún no sea vista de tarjetas (la identidad o el
    // ancho pueden llegar después de que la carga ya empezó).
    if (loadingAseos) cargaIniciadaVista.current = true;
    if (!vistaTarjetas || pestanaInicialResuelta.current) return;
    // Todavía no empezó (o no terminó) la primera carga de aseos.
    if (loadingAseos || !cargaIniciadaVista.current) return;
    pestanaInicialResuelta.current = true;
    const destino = pestanaInicialAseadora({
      porHacer: misAseos.length,
      actual: aseosTab,
      eligioAMano: eligioPestana.current,
    });
    // No se guarda como preferencia: la decide la pantalla, no la persona.
    if (destino) setAseosTabSinRecordar(destino);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistaTarjetas, loadingAseos, misAseos.length]);

  // Enlace de un aviso («N aseos nuevos disponibles para tomar en <sector>», round 2026-10-28):
  // /aseos?tab=abiertos&sector=... aterriza en «Aseos abiertos». La pestaña la impone el enlace
  // (no la eligió la persona): setter que NO guarda, y se marca como elegida para que la pestaña
  // inicial de celular («Por hacer») no la pise. Reacciona a `searchParams` (no solo al montar)
  // porque la aseadora puede tocar un aviso estando ya en /aseos.
  useEffect(() => {
    if (searchParams.get("tab") !== "abiertos" || searchParams.get("aseador")) return;
    eligioPestana.current = true;
    setAseosTabSinRecordar("1");
    const sector = searchParams.get("sector")?.trim();
    if (sector) {
      sectoresDeEnlace.current = new Set(sectoresDeEnlace.current).add(sector);
      setFilterValues((prev) => ({ ...prev, sector: [sector] }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.delete("sector");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // El filtro de sector que se recordó puede apuntar a un sector que ya no existe (se renombró o se borró en
  // Zonas geográficas): la tabla quedaba vacía sin explicación. Si el nombre recordado no figura entre los
  // sectores del catálogo, el filtro vuelve a «todos». Con un enlace directo (`?sector=`) no se toca nada.
  const nombresSectores = useMemo(
    () => sectors.map((s) => s.sector).filter((n): n is string => typeof n === "string" && n !== ""),
    [sectors],
  );
  useSanearFiltroSector({
    catalogo: nombresSectores,
    seleccionados: filterValues.sector,
    aplicar: (vigentes) => setFilterValuesSinRecordar((prev) => ({ ...prev, sector: vigentes })),
    omitir: enlaceDirecto.filtros,
    protegidos: sectoresDeEnlace,
  });

  /**
   * Etiqueta de pestaña. Escritorio/admin: la de siempre. Aseadora en celular: el
   * contador en su propia línea y «Aseos» solo para lectores de pantalla, así las
   * 3 pestañas caben en 412 px (antes «por hacer» quedaba fuera de pantalla).
   */
  const etiquetaPestana = (resto: string, n: number) =>
    vistaTarjetas ? (
      <span className="aseos-tab-movil">
        <span className="aseos-tab-prefijo">Aseos </span>
        <span>{resto} </span>
        <span className="aseos-tab-n">({n})</span>
      </span>
    ) : (
      <span style={{ fontSize: 18, fontWeight: 600 }}>
        Aseos {resto} ({n})
      </span>
    );

  // Limpia los query params de deep-link después de aplicar el filtro inicial
  // (una sola vez). Evita que un refresh con la URL antigua re-aplique el
  // filtro si el usuario ya lo había quitado manualmente.
  useEffect(() => {
    if (
      searchParams.get("sector") ||
      searchParams.get("aseador") ||
      searchParams.get("tab") ||
      searchParams.get("desde") ||
      searchParams.get("hasta")
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete("sector");
      next.delete("aseador");
      next.delete("tab");
      next.delete("desde");
      next.delete("hasta");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [images, setImages] = useState<ImagenCarrusel[]>([]);

  // La columna «Imágenes» pasa solo URLs; cada una se cruza con su entry de
  // `tareas[].imagenes` (que ya trae `previewUrl` y `kind`) para que el video
  // abra en su versión liviana. Ver ./imagenesCarrusel.ts.
  const openImagesModal = (urls: any) => {
    setImages(
      imagenesParaCarrusel(urls, [...aseosDisponibles, ...misAseos, ...aseosTerminados]),
    );
    setIsImageModalVisible(true);
  };

  // Modal de tareas asociadas a la propiedad de un aseo. Pensado para el
  // aseador: necesita poder leer descripciones largas y ver imágenes/videos
  // adjuntos a cada tarea sin pelear con una celda truncada de tabla.
  const [tareasModalVisible, setTareasModalVisible] = useState(false);
  const [tareasModalRecord, setTareasModalRecord] =
    useState<TareasModalRecord | null>(null);

  const openTareasModal = (record: any) => {
    setTareasModalRecord({
      propiedad: record?.propiedad ?? "",
      tareas: Array.isArray(record?.tareas) ? record.tareas : [],
    });
    setTareasModalVisible(true);
  };

  // Modal de las 3 notas del aseo (round 2026-07-05). Reusa AseoNotesSection
  // (role-agnóstico): el admin ve/edita las 3; el aseador ve la "para aseador"
  // y edita la suya. Guardamos el id + un label para el título del modal.
  const [notasAseo, setNotasAseo] = useState<{
    id: number;
    propiedad: string;
    /** Código de estadía AF del arriendo vinculado (reservaId), o null. */
    reservaId: number | null;
  } | null>(null);
  const openNotasModal = (record: any) => {
    if (record?.id == null) return;
    setNotasAseo({
      id: Number(record.id),
      propiedad: record?.propiedad ?? "",
      reservaId: record?.reservaId ?? null,
    });
  };

  // Row-click del aseador (round 2026-07-06): tocar cualquier parte de la fila
  // en "Gestión de aseos" abre el modal de notas del aseo. Más cómodo que
  // apuntar al botón "Notas" del final. Para admin NO aplica (tiene edición
  // inline + varias acciones por fila). Ignoramos clicks en elementos
  // interactivos (botones "Tomar"/"Notas", checkbox de selección, links) para
  // no robarles su acción propia.
  const aseadorRowProps = (record: any) => ({
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      if (!isAseador) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(
          "button, a, input, .ant-btn, .ant-checkbox, [role='button']",
        )
      ) {
        return;
      }
      openNotasModal(record);
    },
    style: isAseador ? { cursor: "pointer" } : undefined,
  });

  // Memoizadas para evitar que los Selects re-rendericen sus opciones en
  // cada render padre.
  const allAseadores = useMemo(
    () => filteredAseadoras.map((a) => ({ value: a.nombre, label: a.nombre })),
    [filteredAseadoras],
  );

  // OJO: options desde `properties` SIN filtrar. Antes usaba
  // getFilteredProperties(), que filtra por el texto del buscador del modal
  // "Seleccionar Propiedad" — como ese texto no se limpiaba, contaminaba las
  // opciones del filtro "Propiedad" de la barra (auditoría 2026-07-13).
  const allPropiedades = useMemo(
    () =>
      properties.map((p) => ({
        value: p.nombre,
        label: formatPropertyName(p.nombre),
      })),
    [properties],
  );
  const sectorSelectOptions = useMemo(
    () => sectors.map((s) => ({ value: s.sector, label: s.sector })),
    [sectors],
  );

  // FilterSort estables: AntD pasa estas funciones al Select como prop. Si
  // las definimos inline en JSX, cambian en cada render y forzan al Select
  // a reordenar/redibujar opciones aunque nada cambie. Con useCallback solo
  // se rebuilden cuando cambian sus selecciones.
  const filterSortAseadores = useCallback(
    (a: any, b: any) => {
      const aSel = filterValues.aseadores.includes(a.value as string);
      const bSel = filterValues.aseadores.includes(b.value as string);
      if (aSel !== bSel) return aSel ? -1 : 1;
      return (a.label as string).localeCompare(b.label as string);
    },
    [filterValues.aseadores],
  );
  const filterSortPropiedad = useCallback(
    (a: any, b: any) => {
      const aSel = filterValues.propiedad.includes(a.value as string);
      const bSel = filterValues.propiedad.includes(b.value as string);
      if (aSel !== bSel) return aSel ? -1 : 1;
      return (a.label as string).localeCompare(b.label as string);
    },
    [filterValues.propiedad],
  );

  const filtrarAseos = (aseos: any, filtros: any) => {
    return aseos.filter((aseo: any) => {
      const {
        fechaInicio,
        aseadores,
        propiedad,
        sector,
        horario,
        sueldoAseador,
        costoLimpieza,
        urgencia,
        estado,
        observaciones,
      } = filtros;

      if (
        fechaInicio[0] &&
        fechaInicio[1] &&
        (dayjs(aseo.fechaInicio).isBefore(fechaInicio[0], "day") ||
          dayjs(aseo.fechaInicio).isAfter(fechaInicio[1], "day"))
      )
        return false;

      // Round 2026-08-25 — con equipo 1..N, el filtro por aseadora matchea si
      // CUALQUIERA del equipo está en la selección (responsable o acompañante).
      if (aseadores.length > 0) {
        const nombresEquipo: string[] = (aseo as any).nombresAseadoras?.length
          ? (aseo as any).nombresAseadoras
          : [aseo.nombreAseador].filter(Boolean);
        if (!nombresEquipo.some((n) => aseadores.includes(n))) return false;
      }

      if (propiedad.length > 0 && !propiedad.includes(aseo.propiedad))
        return false;

      if (sector.length > 0 && !sector.includes(aseo.sector)) return false;

      if (horario[0] && horario[1]) {
        // El filtro guarda strings "HH:mm" — hay que parsearlos CON formato:
        // dayjs("09:00") sin formato es Invalid Date y todas las comparaciones
        // devuelven false, dejando el filtro como no-op silencioso (auditoría
        // 2026-07-13). Un aseo sin hora no puede matchear un rango horario.
        const h = dayjs(aseo.horaInicio, "HH:mm");
        const lo = dayjs(horario[0] as string, "HH:mm");
        const hi = dayjs(horario[1] as string, "HH:mm");
        if (!h.isValid()) return false;
        if (h.isBefore(lo, "minute") || h.isAfter(hi, "minute")) {
          return false;
        }
      }

      if (sueldoAseador[0] !== null && aseo.sueldoAseador < sueldoAseador[0])
        return false;

      if (sueldoAseador[1] !== null && aseo.sueldoAseador > sueldoAseador[1])
        return false;

      if (costoLimpieza[0] !== null && aseo.costoLimpieza < costoLimpieza[0])
        return false;

      if (costoLimpieza[1] !== null && aseo.costoLimpieza > costoLimpieza[1])
        return false;

      if (urgencia.length > 0 && !urgencia.includes(aseo.urgencia))
        return false;

      if (estado.length > 0 && !estado.includes(aseo.estado)) return false;

      if (observaciones) {
        // Buscador de NOTAS + CÓDIGO DE ESTADÍA (round 2026-07-06 / 2026-07-16):
        // busca en las 3 notas del aseo — "para el aseador" (columna
        // observaciones), "privada (solo admin)" y "del aseador". Para el
        // aseador, el backend entrega los TEXTOS de las 2 notas que puede leer
        // (para_aseador + del aseador) en sus aseos y en los abiertos (fix
        // 2026-07-13); la privada nunca sale del server, así que para él la
        // búsqueda cubre esas 2. Además, si el término es "#1472"/"1472",
        // matchea por SUBSTRING (parcial) tanto el Código de aseo propio
        // (`aseo.id` → «Aseo #N») como el Código de estadía AF del arriendo
        // vinculado (`aseo.reservaId` → «Estadía #N»): "142" encuentra 142,
        // 1424, 1420, etc. (pedido del owner 2026-07-16: encontrar parciales,
        // igual que el buscador de /arriendos). OR entre ambos códigos.
        const codigo = parseEstadiaQuery(observaciones);
        const matchCodigo =
          codigo !== "" &&
          (String(aseo.id ?? "").includes(codigo) ||
            String(aseo.reservaId ?? "").includes(codigo));
        const haystack = [
          aseo.observaciones,
          aseo.notaPrivadaAdmin,
          aseo.notaAseador,
        ]
          .filter(Boolean)
          .join("\n");
        const matchTexto = textSimilar(haystack, observaciones);
        if (!matchCodigo && !matchTexto) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredAseos = useMemo(() => {
    // 1) Filtrado por sector (solo para aseadores)
    const sectorFiltered = isAseador
      ? aseosDisponibles.filter((a) =>
          // user.sector puede ser un string o un array de strings,
          // por si acaso cubrimos ambas
          Array.isArray(user?.sector)
            ? user?.sector.includes(a.sector)
            : a.sector === user?.sector,
        )
      : aseosDisponibles;

    // 2) Aplique luego todos los demás filtros de tu función
    return filtrarAseos(sectorFiltered, filterValues);
  }, [aseosDisponibles, filterValues, isAseador, user?.sector]);

  const filteredMisAseos = React.useMemo(() => {
    return filtrarAseos(misAseos, filterValues);
  }, [misAseos, filterValues]);

  const filteredAseosTerminados = React.useMemo(() => {
    return filtrarAseos(aseosTerminados, filterValues);
  }, [aseosTerminados, filterValues]);

  // ─── Round 2026-05-12 iter 39: bulk handlers ─────────────────────────
  // Cada handler recibe la lista de ids seleccionados y dispara la API.
  // Tras éxito, refresca la lista (`fetchAseos`) y limpia la selección
  // del tab correspondiente. Errores manejados por apiFetch (toast).

  const refreshAndClear = (
    clearSetter: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    clearSetter([]);
    fetchAseos();
  };

  const handleBulkEstado = async (
    ids: number[],
    estado: "pendiente" | "en_proceso" | "completado" | "pagado",
    clearSetter: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    if (ids.length === 0) return;
    const res = await aseosApi.bulkEstado(ids, estado);
    if (res?.success) refreshAndClear(clearSetter);
  };

  const handleBulkUrgencia = async (
    ids: number[],
    urgencia: "am" | "pm" | "critico 11:00" | "11:00",
    clearSetter: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    if (ids.length === 0) return;
    const res = await aseosApi.bulkUrgencia(ids, urgencia);
    if (res?.success) refreshAndClear(clearSetter);
  };

  const handleBulkDelete = async (
    ids: number[],
    clearSetter: React.Dispatch<React.SetStateAction<number[]>>,
  ) => {
    if (ids.length === 0) return;
    const res = await aseosApi.bulkDelete(ids);
    if (res?.success) refreshAndClear(clearSetter);
  };

  // Helper para construir el objeto rowSelection de antd. Solo lo
  // habilitamos para admin (los aseadores no tienen permisos bulk).
  const makeRowSelection = (
    selected: number[],
    setSelected: React.Dispatch<React.SetStateAction<number[]>>,
  ): import("antd/es/table/interface").TableRowSelection<any> | undefined => {
    if (!isAdmin) return undefined;
    return {
      selectedRowKeys: selected,
      onChange: (keys) => setSelected(keys.map((k) => Number(k))),
      preserveSelectedRowKeys: false,
    };
  };

  // La gestión de aseadores (tabla + columnas + modal) se movió a la página
  // /aseadores (round 2026-07-06). Aquí solo queda la agenda de aseos.

  const aseosColumns = buildAseosColumns({
    isAdmin,
    isAseador,
    currentUserId: user?.id,
    currentUserNombre: user?.nombre,
    openTareasModal,
    openImagesModal,
    showAseoModal,
    handleDeleteAseo,
    tomarAseo,
    openNotasModal,
    tactil: esTactil,
  });

  const allColumnKeys = aseosColumns.map((col) => col.key as string);
  // Round 2026-05-13 — `rolAF` ya no existe como columna. Solo escondemos
  // por default `fechaFin` (suele coincidir con fechaInicio en aseos cortos).
  const defaultVisible = allColumnKeys.filter((key) => key !== "fechaFin");
  // Se guardan las columnas OCULTAS, no las visibles: así una columna que se
  // agregue más adelante aparece sola para quien ya tenía preferencia guardada
  // (guardando las visibles quedaría escondida para siempre), y una key que ya
  // no existe se descarta.
  const [visibleKeys, setVisibleKeys] = useFiltroPersistido<string[]>(
    "aseos.columnas",
    defaultVisible,
    {
      serializar: (keys) => allColumnKeys.filter((k) => !keys.includes(k)),
      deserializar: (crudo) => {
        if (!Array.isArray(crudo)) return undefined;
        const ocultas = new Set(crudo);
        return allColumnKeys.filter((k) => !ocultas.has(k));
      },
    },
  );

  const displayedColumns = React.useMemo(() => {
    let cols = aseosColumns;
    if (isAseador) {
      // Aseador: vista compacta. Esconder columnas que no le sirven o que
      // pertenecen a admin/empresa. Lo que ve queda en: Propiedad, Fecha
      // inicio, Horario, Pago al aseador, Tareas, Imágenes, Estado,
      // Acciones. Sin esto la tabla quedaba con 12+ columnas y un scroll
      // horizontal enorme — inusable en móvil.
      // Round 2026-05-13 — `quienRecibe` y `rolAF` eliminados del modelo.
      const HIDE_FOR_ASEADOR = new Set([
        "nombreAseador",
        "modoAsignacion",
        "sector",
        "costoLimpieza",
        "quienPaga",
        "fechaFin",
        "observaciones",
        "urgencia",
      ]);
      cols = cols.filter((col) => !HIDE_FOR_ASEADOR.has(col.dataIndex as string));
    }
    if (isAdmin) {
      cols = cols.filter(
        (col) => col.dataIndex !== "imagenes" && col.dataIndex !== "tareas",
      );
    }
    return cols.filter((col) => visibleKeys.includes(col.key as string));
  }, [isAseador, aseosColumns, visibleKeys, isAdmin]);

  const displayedColumnsAbiertos = useMemo(() => {
    return displayedColumns.map((col) => {
      if (col.key === "nombreAseador") {
        return {
          ...col,
          sorter: undefined, // 👈 elimina el sort solo aquí
        };
      }
      return col;
    });
  }, [displayedColumns]);

  // En "Aseos terminados" el ADMIN ahora conserva la columna Acciones
  // (Editar/Eliminar). Antes se ocultaba para todos, dejando los aseos
  // completados/pagados sin forma de reasignar el aseador desde la tabla —
  // la única salida era borrarlos. Editar abre el mismo modal, donde el admin
  // puede cambiar el aseador o pasar el aseo a "Abierto" (= desasignarlo).
  // Para el aseador seguimos ocultando acciones (no actúa sobre terminados).
  // Round 2026-06-23.
  const terminadosColumns = isAdmin
    ? displayedColumns
    : displayedColumns.filter((col) => col.key !== "acciones");

  const isLunes = ahoraChile().day() === 1;
  const fechaInicioWatch = Form.useWatch("fechaInicio", aseoForm);
  const hoy = ahoraChile().startOf("day");
  const fechaInicioDay = fechaInicioWatch
    ? dayjs.isDayjs(fechaInicioWatch)
      ? fechaInicioWatch.startOf("day")
      : dayjs(fechaInicioWatch, "YYYY-MM-DD").startOf("day")
    : null;
  const esPasado = !!fechaInicioDay && fechaInicioDay.isBefore(hoy, "day");
  const esFuturo = !!fechaInicioDay && fechaInicioDay.isAfter(hoy, "day");

  // Round 2026-06-19 (cascada bidireccional D-B). El modal del aseo edita el
  // DESGLOSE de 4 subítems (pago aseador + transporte + materiales +
  // lavandería). El costo total del aseo es la SUMA — lo mostramos en el campo
  // `costoLimpieza` (disabled). Usamos Form.useWatch (reactivo, lee el valor ya
  // commiteado del store) para evitar leer valores stale como pasaría con
  // onChange + getFieldValue.
  const watchAseador = Form.useWatch("sueldoAseador", aseoForm);
  const watchTransporte = Form.useWatch("subTransporte", aseoForm);
  const watchMateriales = Form.useWatch("subMateriales", aseoForm);
  const watchLavanderia = Form.useWatch("subLavanderia", aseoForm);
  const costoTotalAseo =
    (Number(watchAseador) || 0) +
    (Number(watchTransporte) || 0) +
    (Number(watchMateriales) || 0) +
    (Number(watchLavanderia) || 0);
  useEffect(() => {
    // Mantener el campo disabled `costoLimpieza` sincronizado con la suma de
    // los 4 subítems. No genera bucle: la suma no depende de costoLimpieza.
    aseoForm.setFieldsValue({ costoLimpieza: costoTotalAseo });
  }, [costoTotalAseo, aseoForm]);
  // Aseo vinculado a un arriendo → editar el desglose se propaga (cascada).
  // Aseo pagado → desglose congelado (no recalcula para no descuadrar un pago
  // ya emitido); inputs deshabilitados.
  const aseoVinculado = editingAseo?.reservaId != null;
  const aseoPagado = editingAseo?.estado === "pagado";
  useEffect(() => {
    setUiCosto([
      filterValues.costoLimpieza[0] ?? COSTO_MIN,
      filterValues.costoLimpieza[1] ?? COSTO_MAX,
    ]);
  }, [filterValues.costoLimpieza]);

  useEffect(() => {
    setUiSueldo([
      filterValues.sueldoAseador[0] ?? SUELDO_MIN,
      filterValues.sueldoAseador[1] ?? SUELDO_MAX,
    ]);
  }, [filterValues.sueldoAseador]);

  return (
    <ConfigProvider locale={esES}>
      <Layout
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        containerClass="home-container"
        contentClass="home-content"
      >
        <section className="content-section">
          {isAdmin && isLunes && (() => {
            // Regla de pagos (ver docs/flujo-financiero.md §6 y
            // docs/rol-aseador.md §10): el lunes se paga la semana anterior
            // completa (Lun → Dom). El backend `pagarTodo` aplica
            // exactamente este rango. Lo mostramos acá para que el admin
            // sepa qué fechas cubre el botón.
            const lunesPasado = ahoraChile().subtract(7, "day"); // hoy es lunes; -7d = lunes anterior
            const domingoPasado = ahoraChile().subtract(1, "day"); // ayer = domingo
            const rango = `${lunesPasado.format("DD-MM-YYYY")} a ${domingoPasado.format("DD-MM-YYYY")}`;
            return (
              <Alert
                banner
                message={
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: "bold" }}>
                      🚨 ¡Hoy es lunes! Toca pagar a los aseadores 🚨
                    </span>
                    <span style={{ fontSize: 13 }}>
                      Cubre los aseos completados de la semana pasada
                      (<strong>Lun {rango.split(" a ")[0]} → Dom {rango.split(" a ")[1]}</strong>).
                      Usa el botón <strong>"Marcar todo pagado"</strong> en la pestaña
                      "Aseos terminados" para liquidarlos en bloque.
                    </span>
                  </div>
                }
                type="error" // rojo para llamar la atención
                showIcon
                style={{
                  marginBottom: 16,
                  backgroundColor: "#fff1f0", // fondo muy claro tirando a rojo
                  borderColor: "#ffa39e", // borde en tono fuerte
                }}
              />
            );
          })()}

          {/* Agenda de aseos — única sección de /aseos desde round 2026-07-06.
              La gestión de aseadores se movió a la página /aseadores (Gestión),
              por eso ya no hay Segmented de sub-tabs acá. */}
          <>
              <div style={{ marginTop: isAdmin ? "16px" : 0 }}>
                <PageHeader
                  title="Gestión de Aseos"
                  helpText={
                    isAdmin
                      ? "Gestión de aseos y aseadores. Usa la vista calendario para ver aseos por día."
                      : "Tus aseos asignados y abiertos. Para verlos por día usa el menú «Calendario»."
                  }
                  extra={
                    // El toggle Tabla/Calendario es SOLO admin (round 2026-07-06).
                    // El aseador tiene su propio calendario dedicado en el menú
                    // («Calendario» → /calendario-aseador), más completo; tener
                    // dos calendarios (este tab + el del menú) confundía.
                    isAdmin ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <Button.Group>
                          <Button
                            type={aseosViewMode === "tabla" ? "primary" : "default"}
                            icon={<UnorderedListOutlined />}
                            onClick={() => setAseosViewMode("tabla")}
                          >
                            Tabla
                          </Button>
                          <Button
                            type={
                              aseosViewMode === "calendario" ? "primary" : "default"
                            }
                            icon={<CalendarOutlined />}
                            onClick={() => setAseosViewMode("calendario")}
                          >
                            Calendario
                          </Button>
                        </Button.Group>
                      </div>
                    ) : undefined
                  }
                />
                <div className="header-buttons">
                  {isAdmin && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => showAseoModal()}
                      style={{ marginRight: "10px", marginBottom: "8px" }}
                    >
                      Nuevo Aseo
                    </Button>
                  )}
                </div>
              </div>
              {isAdmin && aseosViewMode === "calendario" && <AseosCalendar />}

              {(!isAdmin || aseosViewMode === "tabla") && (
            <>
              {/* Barra de filtros RÁPIDOS (siempre visible): los filtros más
                  usados a un clic, sin abrir el acordeón. Comparte el estado
                  `filterValues` con el panel avanzado. */}
              <QuickAseoFilters
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                allAseadores={allAseadores}
                allPropiedades={allPropiedades}
                sectorSelectOptions={sectorSelectOptions}
                filterSortAseadores={filterSortAseadores}
                filterSortPropiedad={filterSortPropiedad}
                showAseadorFilter={!isAseador}
              />
              {/* Panel avanzado (colapsable): rango exacto de fechas, horario,
                  montos y urgencia. */}
              <AseoFilterBar
                filterValues={filterValues}
                setFilterValues={setFilterValues}
                panelOpen={filterPanelOpen}
                setPanelOpen={setFilterPanelOpen}
                showCostoLimpieza={!isAseador}
              />
              {/* Chips de filtros activos: visibles aunque el acordeón esté
                  cerrado, removibles uno a uno + "Limpiar todo". */}
              <ActiveAseoFilters
                filterValues={filterValues}
                setFilterValues={setFilterValues}
              />
              <Tabs
                activeKey={aseosTab}
                onChange={cambiarPestana}
                type="card"
                tabBarGutter={vistaTarjetas ? 4 : 24}
                className={vistaTarjetas ? "aseos-tabs-movil" : undefined}
              >
                <TabPane
                  key="1"
                  tab={etiquetaPestana("abiertos", filteredAseos.length)}
                >
                  {isAdmin && (
                    <Popover
                      content={
                        <Checkbox.Group
                          options={aseosColumns
                            .filter(
                              (col) =>
                                !HIDDEN_COLUMN_KEYS.includes(col.key as string),
                            )
                            .map((col) => ({
                              label: col.title as string,
                              value: col.key as string,
                            }))}
                          value={visibleKeys.filter(
                            (k) => !HIDDEN_COLUMN_KEYS.includes(k),
                          )}
                          onChange={(ks) => {
                            const next = ks as string[];

                            // Si el usuario también es aseador, forzamos que tareas/imagenes
                            // queden siempre activas aunque no estén en el selector.
                            const forced = isAseador ? HIDDEN_COLUMN_KEYS : [];

                            setVisibleKeys(
                              Array.from(new Set([...next, ...forced])),
                            );
                          }}
                        />
                      }
                      title="Mostrar columnas"
                      trigger="click"
                    >
                      <Button
                        icon={<SettingOutlined />}
                        style={{ marginBottom: 8 }}
                      >
                        Columnas
                      </Button>
                    </Popover>
                  )}
                  {/* Round 2026-05-12 iter 39: barra de acciones bulk
                      visible cuando hay filas seleccionadas. */}
                  {/* Ventana de 14 días (round 2026-10-28): la aseadora solo ve los abiertos
                      de los próximos 14 días; el admin ve todos y no necesita el aviso. */}
                  {!isAdmin && <AvisoVentanaAbiertos dias={ventanaDias} />}
                  <BulkActionsBar
                    selectedIds={selectedAbiertos}
                    onClear={() => setSelectedAbiertos([])}
                    onChangeEstado={(e) =>
                      handleBulkEstado(selectedAbiertos, e, setSelectedAbiertos)
                    }
                    onChangeUrgencia={(u) =>
                      handleBulkUrgencia(selectedAbiertos, u, setSelectedAbiertos)
                    }
                    onDelete={() =>
                      handleBulkDelete(selectedAbiertos, setSelectedAbiertos)
                    }
                  />
                  {vistaTarjetas ? (
                    <AseoTarjetasMovil
                      data={filteredAseos}
                      columns={aseosColumns}
                      loading={loadingAseos}
                      vacio="No hay aseos abiertos por ahora."
                      onAbrir={openNotasModal}
                    />
                  ) : (
                    <Table
                      className="aseos-table"
                      rowKey="id"
                      scroll={{ x: isAseador ? "max-content" : ANCHO_TABLA_ADMIN }}
                      loading={loadingAseos}
                      onRow={aseadorRowProps}
                      dataSource={filteredAseos}
                      columns={displayedColumnsAbiertos}
                      rowSelection={makeRowSelection(
                        selectedAbiertos,
                        setSelectedAbiertos,
                      )}
                      pagination={{
                        current: pageAbiertos,
                        pageSize: pageSizeAbiertos,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        onChange: (p) => setPageAbiertos(p),
                        onShowSizeChange: (_current, size) => {
                          setPageSizeAbiertos(size);
                          setPageAbiertos(1);
                        },
                      }}
                    />
                  )}
                </TabPane>

                <TabPane
                  key="2"
                  tab={etiquetaPestana("por hacer", filteredMisAseos.length)}
                >
                  {isAdmin && (
                    <Popover
                      content={
                        <Checkbox.Group
                          options={aseosColumns
                            .filter(
                              (col) =>
                                !HIDDEN_COLUMN_KEYS.includes(col.key as string),
                            )
                            .map((col) => ({
                              label: col.title as string,
                              value: col.key as string,
                            }))}
                          value={visibleKeys.filter(
                            (k) => !HIDDEN_COLUMN_KEYS.includes(k),
                          )}
                          onChange={(ks) => {
                            const next = ks as string[];

                            // Si el usuario también es aseador, forzamos que tareas/imagenes
                            // queden siempre activas aunque no estén en el selector.
                            const forced = isAseador ? HIDDEN_COLUMN_KEYS : [];

                            setVisibleKeys(
                              Array.from(new Set([...next, ...forced])),
                            );
                          }}
                        />
                      }
                      title="Mostrar columnas"
                      trigger="click"
                    >
                      <Button
                        icon={<SettingOutlined />}
                        style={{ marginBottom: 8 }}
                      >
                        Columnas
                      </Button>
                    </Popover>
                  )}
                  <BulkActionsBar
                    selectedIds={selectedPorHacer}
                    onClear={() => setSelectedPorHacer([])}
                    onChangeEstado={(e) =>
                      handleBulkEstado(selectedPorHacer, e, setSelectedPorHacer)
                    }
                    onChangeUrgencia={(u) =>
                      handleBulkUrgencia(selectedPorHacer, u, setSelectedPorHacer)
                    }
                    onDelete={() =>
                      handleBulkDelete(selectedPorHacer, setSelectedPorHacer)
                    }
                  />
                  {vistaTarjetas ? (
                    <AseoTarjetasMovil
                      data={filteredMisAseos}
                      columns={aseosColumns}
                      loading={loadingAseos}
                      vacio="No tienes aseos por hacer."
                      onAbrir={openNotasModal}
                    />
                  ) : (
                    <Table
                      className="aseos-table"
                      scroll={{ x: isAseador ? "max-content" : ANCHO_TABLA_ADMIN }}
                      rowKey="id"
                      loading={loadingAseos}
                      onRow={aseadorRowProps}
                      dataSource={filteredMisAseos}
                      columns={displayedColumns}
                      rowSelection={makeRowSelection(
                        selectedPorHacer,
                        setSelectedPorHacer,
                      )}
                      pagination={{
                        current: pagePorHacer,
                        pageSize: pageSizeAbiertos,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        onChange: (p) => setPagePorHacer(p),
                        onShowSizeChange: (_current, size) => {
                          setPageSizeAbiertos(size);
                          setPagePorHacer(1);
                        },
                      }}
                    />
                  )}
                </TabPane>

                <TabPane
                  key="3"
                  tab={etiquetaPestana("terminados", filteredAseosTerminados.length)}
                >
                  {isAdmin && (
                    <Popover
                      content={
                        <Checkbox.Group
                          options={aseosColumns
                            .filter(
                              (col) =>
                                !HIDDEN_COLUMN_KEYS.includes(col.key as string),
                            )
                            .map((col) => ({
                              label: col.title as string,
                              value: col.key as string,
                            }))}
                          value={visibleKeys.filter(
                            (k) => !HIDDEN_COLUMN_KEYS.includes(k),
                          )}
                          onChange={(ks) => {
                            const next = ks as string[];

                            // Si el usuario también es aseador, forzamos que tareas/imagenes
                            // queden siempre activas aunque no estén en el selector.
                            const forced = isAseador ? HIDDEN_COLUMN_KEYS : [];

                            setVisibleKeys(
                              Array.from(new Set([...next, ...forced])),
                            );
                          }}
                        />
                      }
                      title="Mostrar columnas"
                      trigger="click"
                    >
                      <Button
                        icon={<SettingOutlined />}
                        style={{ marginBottom: 8 }}
                      >
                        Columnas
                      </Button>
                    </Popover>
                  )}
                  {/* Botón solo en esta pestaña */}
                  {isAdmin && (
                    <div style={{ marginBottom: 16, textAlign: "right" }}>
                      <Button
                        type="primary"
                        onClick={handleMarcarTodoPagado}
                        loading={processingPago}
                      >
                        Marcar todo pagado
                      </Button>
                    </div>
                  )}

                  <BulkActionsBar
                    selectedIds={selectedTerminados}
                    onClear={() => setSelectedTerminados([])}
                    onChangeEstado={(e) =>
                      handleBulkEstado(
                        selectedTerminados,
                        e,
                        setSelectedTerminados,
                      )
                    }
                    onChangeUrgencia={(u) =>
                      handleBulkUrgencia(
                        selectedTerminados,
                        u,
                        setSelectedTerminados,
                      )
                    }
                    onDelete={() =>
                      handleBulkDelete(selectedTerminados, setSelectedTerminados)
                    }
                  />
                  {vistaTarjetas ? (
                    <AseoTarjetasMovil
                      data={filteredAseosTerminados}
                      columns={aseosColumns}
                      loading={loadingAseos}
                      vacio="Aún no tienes aseos terminados."
                      onAbrir={openNotasModal}
                      sinAcciones
                    />
                  ) : (
                    <Table
                      className="aseos-table"
                      scroll={{ x: isAseador ? "max-content" : ANCHO_TABLA_ADMIN }}
                      rowKey="id"
                      loading={loadingAseos}
                      onRow={aseadorRowProps}
                      dataSource={filteredAseosTerminados}
                      columns={terminadosColumns}
                      rowSelection={makeRowSelection(
                        selectedTerminados,
                        setSelectedTerminados,
                      )}
                      pagination={{
                        current: pageTerminados,
                        pageSize: pageSizeAbiertos,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                        onChange: (p) => setPageTerminados(p),
                        onShowSizeChange: (_current, size) => {
                          setPageSizeAbiertos(size);
                          setPageTerminados(1);
                        },
                      }}
                    />
                  )}
                </TabPane>
              </Tabs>
            </>
          )}
            </>

          {/* Modal for adding/editing aseos */}
          {isAdmin && (
            <Modal
              title={editingAseo ? "Editar aseo" : "Nuevo aseo"}
              open={aseoModalVisible}
              onOk={handleAseoSubmit}
              onCancel={handleAseoCancel}
              okText={editingAseo ? "Actualizar" : "Crear"}
              cancelText="Cancelar"
              // Deshabilita el botón OK + spinner mientras crea/edita (y sube
              // las notas staged). Evita el doble-submit. Round 2026-07-06.
              // Con confirmLoading activo AntD además ignora Cancelar, la X,
              // ESC y el clic afuera: por eso el modal no se cierra mientras se
              // espera el aviso de aseos el mismo día (round 2026-09-11).
              confirmLoading={submittingAseo}
              width={1100}
            >
              <Form
                form={aseoForm}
                layout="vertical"
                initialValues={{
                  // Round 2026-05-13 — único campo de flujo financiero del
                  // aseo. `pagado_a` y `rolAF` fueron eliminados (los
                  // destinatarios son implícitos según el flujo del aseo,
                  // ver Alert "Flujo financiero del aseo" abajo y
                  // docs/flujo-financiero.md §6.5).
                  pagado_por: {
                    tipo: 0,
                    nombre: "Aloja Fácil",
                    usuario_id: null,
                  },
                }}
              >
                <div className="flex-container">
                  {/* Add mode selector first */}
                  <Form.Item
                    name="modoAsignacion"
                    label="Modo de asignación"
                    initialValue="designado"
                  >
                    <Select
                      onChange={(value) => {
                        setAseoAssignmentMode(value as "designado" | "abierto");
                        // Round 2026-05-13: si vuelve a "abierto",
                        // blanqueamos la asignación completa (round 2026-08-25:
                        // también el equipo y el reparto multi-aseadora).
                        if (value === "abierto") {
                          aseoForm.setFieldsValue({
                            idAseador: undefined,
                            nombreAseador: undefined,
                            idAseadores: [],
                            asignaciones: [],
                          });
                        }
                      }}
                    >
                      <Option value="designado">
                        Designado (aseador específico)
                      </Option>
                      <Option value="abierto">
                        Abierto (cualquier aseador disponible)
                      </Option>
                    </Select>
                  </Form.Item>

                  {/* Round 2026-08-25 — asignación 1..N aseadoras + reparto
                      del pago (partes iguales por defecto, editable). El
                      componente gobierna idAseadores/asignaciones y mantiene
                      los espejos legacy idAseador/nombreAseador. */}
                  {aseoAssignmentMode === "designado" && (
                    <RepartoAseadorasField
                      form={aseoForm}
                      aseadoras={filteredAseadoras}
                      totalFieldName="sueldoAseador"
                      disabled={aseoPagado}
                      // Aviso «ya tiene otro aseo ese día» (round 2026-09-11).
                      avisoMismoDia={{
                        campoDesde: "fechaInicio",
                        campoHasta: "fechaFin",
                        excluirAseoId: editingAseo?.id ?? null,
                      }}
                    />
                  )}

                  <Form.Item name="idAseador" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="nombreAseador" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="idPropiedad" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="propiedad" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="sector" hidden>
                    <Input />
                  </Form.Item>

                  {/* Property selector */}
                  <Form.Item
                    className="full-width"
                    label="Propiedad"
                    required
                    help={
                      selectedProperty ? (
                        <>
                          <div style={{ color: "#389e0d" }}>
                            Pago sugerido: $
                            {selectedProperty.costoLimpieza?.toLocaleString(
                              "es-CL",
                            )}
                          </div>
                        </>
                      ) : (
                        "Seleccione una propiedad para continuar"
                      )
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <Button
                        type="default"
                        onClick={openPropertySelector}
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {selectedProperty ? (
                          <span>{formatPropertyName(selectedProperty.nombre)}</span>
                        ) : (
                          <span style={{ color: "#bfbfbf" }}>
                            Seleccionar propiedad
                          </span>
                        )}
                        <PlusOutlined />
                      </Button>
                      {selectedProperty && (
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            aseoForm.setFieldsValue({
                              idPropiedad: undefined,
                              propiedad: undefined,
                              sector: undefined,
                              costoLimpieza: undefined,
                              sueldoAseador: undefined,
                              subTransporte: undefined,
                              subMateriales: undefined,
                              subLavanderia: undefined,
                            });
                          }}
                        />
                      )}
                    </div>
                  </Form.Item>

                  {/* Aviso de qué significa editar el desglose (mandato de
                      claridad del owner — round 2026-06-19, cascada D-B). El
                      texto cambia según el aseo esté vinculado a un arriendo,
                      pagado, o sea manual. */}
                  {aseoPagado ? (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Aseo pagado: desglose congelado"
                      description="Este aseo ya fue pagado. Modificar los montos aquí no recalcula el arriendo ni la liquidación del cliente, para no descuadrar un pago ya emitido."
                    />
                  ) : aseoVinculado ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={`Este aseo pertenece a la Estadía ${formatEstadiaCode(editingAseo?.reservaId)}`}
                      description="Al guardar, el desglose se propaga automáticamente al arriendo y recalcula su costo de limpieza, la liquidación del cliente (lo que se descuenta al propietario) y el lavado vinculado. No hay que editar lo mismo en varios lugares: con cambiarlo aquí queda todo coherente."
                    />
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Aseo manual (sin arriendo asociado)"
                      description="El desglose solo afecta a este aseo y a lo que recibe el aseador. No hay arriendo ni liquidación de cliente que recalcular."
                    />
                  )}

                  {/* Desglose de los 4 subítems. Son la FUENTE del costo: el
                      total (campo de abajo) es la suma y está deshabilitado. */}
                  <Form.Item
                    name="sueldoAseador"
                    label={<span>Pago al aseador</span>}
                    rules={[
                      {
                        required: true,
                        message: "Por favor ingrese el pago al aseador",
                      },
                    ]}
                    tooltip="Lo que recibe el aseador por este aseo. Es uno de los 4 subítems del costo. Viene pre-rellenado con el valor de la propiedad, pero se puede modificar."
                  >
                    <Input
                      type="number"
                      addonBefore="$"
                      placeholder="Ej: 15000"
                      min={0}
                      disabled={aseoPagado}

                      style={{
                        borderColor: selectedProperty ? "#52c41a" : undefined,
                        backgroundColor: selectedProperty
                          ? "#f6ffed"
                          : undefined,
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="subTransporte"
                    label={<span>Transporte</span>}
                    tooltip="Costo de transporte del aseo (uno de los 4 subítems). Si lo paga el propietario o arrendatario, su neto entra como ingreso de Aloja Fácil."
                  >
                    <Input
                      type="number"
                      addonBefore="$"
                      placeholder="Ej: 3000"
                      min={0}
                      disabled={aseoPagado}

                    />
                  </Form.Item>

                  <Form.Item
                    name="subMateriales"
                    label={<span>Materiales</span>}
                    tooltip="Costo de materiales del aseo (uno de los 4 subítems). Si lo paga el propietario o arrendatario, su neto entra como ingreso de Aloja Fácil."
                  >
                    <Input
                      type="number"
                      addonBefore="$"
                      placeholder="Ej: 2000"
                      min={0}
                      disabled={aseoPagado}

                    />
                  </Form.Item>

                  <Form.Item
                    name="subLavanderia"
                    label={<span>Lavandería</span>}
                    tooltip="Costo de lavandería del aseo (uno de los 4 subítems). Se registra como lavado vinculado en /lavanderia."
                  >
                    <Input
                      type="number"
                      addonBefore="$"
                      placeholder="Ej: 5000"
                      min={0}
                      disabled={aseoPagado}

                    />
                  </Form.Item>

                  <Form.Item
                    name="costoLimpieza"
                    label={<span>Costo total del aseo (suma de los 4 subítems)</span>}
                    rules={[
                      {
                        required: true,
                        message: "Por favor complete el desglose del aseo",
                      },
                    ]}
                    tooltip="Se calcula automáticamente como la suma de los 4 subítems (pago al aseador + transporte + materiales + lavandería). No es editable directamente."
                  >
                    <Input
                      type="number"
                      addonBefore="$"
                      disabled
                      style={{
                        borderColor: selectedProperty ? "#52c41a" : undefined,
                        backgroundColor: selectedProperty
                          ? "#f6ffed"
                          : undefined,
                        fontWeight: "bold",
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="fechaInicio"
                    label="Fecha inicio"
                    rules={[
                      {
                        required: true,
                        message: "Por favor seleccione la fecha de inicio",
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      inputReadOnly
                      onChange={(date) => {
                        if (!date) return;
                        const fin = aseoForm.getFieldValue("fechaFin");
                        if (!fin || date.isAfter(fin, "day")) {
                          aseoForm.setFieldsValue({ fechaFin: date });
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="fechaFin"
                    label="Fecha fin"
                    rules={[
                      {
                        required: true,
                        message: "Por favor seleccione la fecha de término",
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                      inputReadOnly
                    />
                  </Form.Item>
                  <Form.Item
                    name="horaInicio"
                    label="Hora de inicio"
                    rules={[
                      {
                        required: true,
                        message: "Por favor seleccione la hora de inicio",
                      },
                    ]}
                  >
                    <TimePicker
                      style={{ width: "100%" }}
                      format="HH:mm"
                      minuteStep={5}
                      inputReadOnly
                      placeholder="Selecciona hora"
                    />
                  </Form.Item>

                  <Form.Item
                    name="urgencia"
                    label="Urgencia"
                    rules={[
                      {
                        required: true,
                        message: "Por favor seleccione la urgencia",
                      },
                    ]}
                  >
                    <Select placeholder="Seleccione la urgencia">
                      <Option value="am">AM</Option>
                      <Option value="pm">PM</Option>
                      <Option value="critico 11:00">Crítico 11:00</Option>
                      <Option value="11:00">11:00</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="estado"
                    label="Estado"
                    rules={[
                      {
                        required: true,
                        message: "Por favor seleccione el estado",
                      },
                    ]}
                  >
                    <Select placeholder="Seleccione el estado">
                      <Select.Option value="pendiente" disabled={esPasado}>
                        Pendiente
                      </Select.Option>

                      <Select.Option value="en_proceso" disabled={esPasado}>
                        En proceso
                      </Select.Option>

                      <Select.Option value="completado" disabled={esFuturo}>
                        Completado
                      </Select.Option>

                      <Select.Option value="pagado" disabled={esFuturo}>
                        Pagado
                      </Select.Option>

                      <Select.Option value="cancelado" disabled={esFuturo}>
                        Cancelado
                      </Select.Option>
                    </Select>
                  </Form.Item>

                  {/* Round 2026-05-13 — Flujo financiero simplificado.
                      Eliminamos los campos "Rol de AF" y "Pagado a" porque
                      los destinatarios del aseo son IMPLÍCITOS y FIJOS:
                        • Aseador asignado SIEMPRE recibe `sueldo_aseador`.
                        • Lavandería asociada SIEMPRE recibe `lavandería`
                          (subitem, registrado automático en /lavanderia).
                        • AF Empresa retiene `transporte + materiales` SI el
                          pagador es propietario o arrendatario; egresa los
                          4 subitems si AF paga directo.
                      Solo queda "Pagado por" (Quién paga). Ver Alert
                      explicativo + docs/flujo-financiero.md §6.5. */}
                  <div
                    className="full-width"
                    style={{
                      marginTop: 12,
                      padding: 16,
                      border: "1px solid #d9d9d9",
                      borderRadius: 8,
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>
                        💸 Flujo financiero del aseo
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        Solo define <strong>quién paga</strong>. Los flujos de
                        dinero son siempre los mismos:
                      </div>
                    </div>

                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          Cómo fluye el dinero en un aseo
                        </span>
                      }
                      description={
                        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                          <div>• <strong>Pago aseador</strong> → siempre va íntegro al aseador asignado.</div>
                          <div>• <strong>Lavandería</strong> → siempre va a la lavandería asociada (AF interna o externa). Se registra automáticamente en /lavanderia.</div>
                          <div>• <strong>Transporte + Materiales</strong> → ingreso para Aloja Fácil <em>si</em> paga propietario o arrendatario. Si paga AF directo, los 4 subitems salen de su caja.</div>
                          <div style={{ marginTop: 6, color: "#6b7280" }}>
                            Más detalle en <em>docs/flujo-financiero.md §6.5</em>.
                          </div>
                        </div>
                      }
                    />

                    <Form.Item
                      label="Pagado por (origen)"
                      tooltip="Quién paga el aseo. Las opciones son las únicas relevantes para el flujo: AF Empresa (paga de su caja), Propietario (se cobra al dueño) o Arrendatario (lo paga el huésped dentro de la reserva)."
                    >
                      <Form.Item
                        name={["pagado_por", "tipo"]}
                        noStyle
                        rules={[{ required: true, message: "Selecciona quién paga" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          onChange={(val: number) => {
                            const nombres: Record<number, string> = {
                              0: "Aloja Fácil",
                              // tipo=1 = Cliente-gestor (E3 / D-A, 2026-06-20).
                              1: "Cliente",
                              2: "Arrendatario",
                            };
                            aseoForm.setFieldsValue({
                              pagado_por: {
                                tipo: val,
                                nombre: nombres[val] ?? "",
                                usuario_id: null,
                              },
                            });
                          }}
                        >
                          <Select.Option value={0}>Aloja Fácil</Select.Option>
                          {/* tipo=1 = Cliente-gestor (regla canónica D-A,
                              2026-06-20): el label visible debe coincidir con
                              el mapa `nombres` de arriba ("Cliente"), no
                              "Propietario". Guarda anti-doble-cobro
                              (flujo-financiero.md §6.4): si el aseo está
                              VINCULADO a un arriendo, "Cliente" se deshabilita
                              porque su limpieza ya se le descuenta al cliente —
                              cobrárselo de nuevo sería doble cobro (el backend
                              también lo rechaza). */}
                          <Select.Option value={1} disabled={aseoVinculado}>
                            Cliente
                          </Select.Option>
                          <Select.Option value={2}>Arrendatario</Select.Option>
                        </Select>
                      </Form.Item>
                      {aseoVinculado && (
                        <div
                          style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}
                        >
                          Este aseo está <b>vinculado a un arriendo</b>: su
                          limpieza ya se le descuenta al cliente en la
                          liquidación. Por eso no se le puede cobrar de nuevo
                          (opción "Cliente" deshabilitada). Si es un servicio
                          aparte, desvincúlalo del arriendo primero.
                        </div>
                      )}
                    </Form.Item>
                  </div>

                  {/* ── Notas del aseo (3 notas con adjuntos) ──────────────
                      Round 2026-07-06: reemplaza el antiguo textarea
                      "Observaciones" (que escribía la MISMA columna que la
                      "Nota para el aseador" → redundancia). Ahora las 3 notas
                      se editan aquí mismo, con texto + fotos/videos/documentos.
                      - Al EDITAR: modo persistido (guarda al instante por nota).
                      - Al CREAR: modo borrador (texto + archivos staged); se
                        persisten al confirmar, cuando el aseo ya tiene id.
                      `key` con el nonce → remonta limpio en cada apertura. */}
                  <div
                    className="full-width"
                    style={{
                      marginTop: 12,
                      padding: 16,
                      border: "1px solid #d9d9d9",
                      borderRadius: 8,
                      background: "#fff",
                    }}
                  >
                    <div style={{ marginBottom: 4 }}>
                      <span
                        style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}
                      >
                        📝 Notas del aseo
                      </span>
                      <div
                        style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}
                      >
                        {editingAseo
                          ? "Cada nota se guarda por separado con su propio botón/subida. La nota privada solo la ves tú (admin); la del aseador la puede ver y editar el aseador."
                          : "Puedes escribir y adjuntar fotos, videos o documentos ahora; se guardan junto con el aseo al crearlo."}
                      </div>
                    </div>
                    <AseoNotesSection
                      key={`aseo-notes-${editingAseo?.id ?? "new"}-${aseoModalNonce}`}
                      aseoId={editingAseo ? editingAseo.id : null}
                      draftRef={editingAseo ? undefined : notesDraftRef}
                    />
                  </div>
                </div>
              </Form>
            </Modal>
          )}

          {/* New Modal for Property Selection */}
          {isAdmin && (
            <Modal
              title="Seleccionar Propiedad"
              open={propertyModalVisible}
              onCancel={() => {
                setPropertyModalVisible(false);
                // Limpia el texto para no dejar el buscador del modal
                // "pegado" la próxima vez que se abra.
                setPropertySearchText("");
              }}
              footer={null}
              width={800}
            >
              <div style={{ marginBottom: 16 }}>
                <Input.Search
                  placeholder="Buscar por nombre"
                  allowClear
                  enterButton="Buscar"
                  size="large"
                  value={propertySearchText}
                  onChange={(e) => setPropertySearchText(e.target.value)}
                />
              </div>

              {loadingProperties ? (
                <div style={{ textAlign: "center", padding: "24px" }}>
                  <div className="loading-spinner"></div>
                  <p>Cargando propiedades...</p>
                </div>
              ) : (
                <div className="property-grid">
                  {getFilteredProperties().map((property) => (
                    <div
                      key={property.id}
                      className="property-card"
                      onClick={() => handleSelectProperty(property)}
                    >
                      <div className="property-icon">
                        {property.tipo === "Casa"
                          ? "🏠"
                          : property.tipo === "Apartamento"
                            ? "🏢"
                            : "🏢"}
                      </div>
                      <div className="property-info">
                        <div className="property-name">
                          {formatPropertyName(property.nombre)}
                        </div>
                        <div className="property-details">
                          {property.tipo} - {property.habitaciones} habs -{" "}
                          {property.banos} baños
                        </div>
                        <div
                          style={{
                            fontWeight: "bold",
                            color: "#389e0d",
                            marginTop: "5px",
                          }}
                        >
                          Pago sugerido: $
                          {property.costoLimpieza
                            ? property.costoLimpieza.toLocaleString("es-CL")
                            : "No definido"}
                        </div>
                      </div>
                    </div>
                  ))}

                  {getFilteredProperties().length === 0 && (
                    <div className="no-properties-message">
                      <p>
                        No se encontraron propiedades que coincidan con tu
                        búsqueda.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Modal>
          )}
        </section>
        <ImageCarouselModal
          open={isImageModalVisible}
          images={images}
          onClose={() => {
            setIsImageModalVisible(false);
            setImages([]);
          }}
        />

        <TareasViewModal
          open={tareasModalVisible}
          record={tareasModalRecord}
          onClose={() => setTareasModalVisible(false)}
        />

        {/* Modal de las 3 notas del aseo (round 2026-07-05). AseoNotesSection
            es role-agnóstico: el admin ve/edita las 3; el aseador ve la nota
            "para el aseador" y edita la suya (texto + fotos/videos).
            Al cerrar se refresca la lista: las notas se guardan por su
            endpoint propio (sin broadcast WS), así que sin el refetch el
            "Buscar en notas" no encontraba texto recién escrito hasta
            recargar la página (auditoría 2026-07-13). */}
        <NotasContenedor
          open={notasAseo !== null}
          aseoId={notasAseo?.id}
          title={
            notasAseo ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {/* notasAseo.id ES el código propio del aseo («Aseo #N»); el
                    código de estadía (del arriendo vinculado) va en el Tag
                    «Estadía #N» de al lado cuando corresponde. */}
                <span>
                  Notas del aseo —{" "}
                  {formatPropertyName(notasAseo.propiedad) || "propiedad —"}
                </span>
                <AseoCodeTag id={notasAseo.id} />
                {notasAseo.reservaId != null && (
                  <EstadiaCodeTag id={notasAseo.reservaId} />
                )}
              </span>
            ) : (
              "Notas del aseo"
            )
          }
          onClose={() => {
            setNotasAseo(null);
            void fetchAseos();
          }}
          width={680}
        >
          {notasAseo && <AseoNotesSection aseoId={notasAseo.id} />}
        </NotasContenedor>
      </Layout>
    </ConfigProvider>
  );
};

export default AseosPage;
